import { Profile, Role } from '../types';
import { actorForShow, ShowActor } from './showActor';

/**
 * A discrete thing a user may be allowed to do.
 *
 * A port of `mobile_app/lib/permissions/capability.dart` and
 * `capability_resolver.dart`, plus the web-only surfaces mobile does not have
 * (Bulk Send). The per-capability doc comments there name the backend gate
 * each row mirrors; they are not repeated here. `src/__tests__/permissions`
 * pins this table against the mobile one so the two cannot drift.
 *
 * **These are UX gates only.** Every one is re-checked by the backend, so a
 * client that says yes still gets `permission-denied` if the server disagrees.
 * Never widen a row ahead of the backend.
 */
export type Capability =
  // global
  | 'createShow'
  | 'deleteOwnAccount'
  | 'sendBroadcast'
  // show-scoped
  | 'manageShow'
  | 'deleteShow'
  | 'manageCast'
  | 'addCastStudent'
  | 'viewCast'
  | 'viewCastEmail'
  | 'manageCastAccounts'
  | 'editShowDetails'
  | 'createRehearsal'
  | 'manageRehearsals'
  | 'createShowFolder'
  | 'manageShowFolders'
  | 'uploadShowResource'
  | 'editShowResource'
  | 'deleteShowResource'
  | 'uploadGalleryPhoto'
  | 'createGalleryFolder'
  | 'reorderGalleryFolders'
  | 'createShowPost'
  | 'pinShowPost'
  | 'moderateShowPost'
  | 'moderateGalleryPhoto'
  | 'createGroupChat';

/** Actors that satisfy the backend's `canManageShow`. A set, not a threshold. */
const CAN_MANAGE_SHOW: ReadonlySet<ShowActor> = new Set<ShowActor>(['showStaff', 'orgAdmin', 'superAdmin']);

/** Staff plus the student leader of this show. */
const STAFF_OR_LEADER: ReadonlySet<ShowActor> = new Set<ShowActor>([
  'showStaff',
  'orgAdmin',
  'superAdmin',
  'showLeader',
]);

/** Anyone with standing on the show. Mirrors `validateUserAssignedToShow`. */
const ANY_SHOW_MEMBER: ReadonlySet<ShowActor> = new Set<ShowActor>([
  'student',
  'showLeader',
  'showStaff',
  'orgAdmin',
  'superAdmin',
]);

/**
 * Who holds each show-scoped capability. **This table is the dial** - to let
 * leaders do something new, widen the backend predicate first, then that one
 * row here and in `capability_resolver.dart`, then the truth-table tests.
 */
export const SHOW_GRANTS: Readonly<Record<string, ReadonlySet<ShowActor>>> = {
  manageShow: CAN_MANAGE_SHOW,
  // deleteShow (canDeleteShow in permissions.ts) needs an org admin of the
  // show's organisation, or a super admin - not every teacher who manages it.
  deleteShow: new Set<ShowActor>(['orgAdmin', 'superAdmin']),
  manageCast: CAN_MANAGE_SHOW,
  manageCastAccounts: CAN_MANAGE_SHOW,
  manageRehearsals: CAN_MANAGE_SHOW,

  // Library content is flat for leaders: they may rename or delete anything in
  // the show's library, a teacher's included.
  uploadShowResource: STAFF_OR_LEADER,
  editShowResource: STAFF_OR_LEADER,
  deleteShowResource: STAFF_OR_LEADER,
  createShowFolder: STAFF_OR_LEADER,
  manageShowFolders: STAFF_OR_LEADER,

  createShowPost: STAFF_OR_LEADER,
  // Pinning is curation and flat; deleting (below) is rank-aware.
  pinShowPost: STAFF_OR_LEADER,
  // Necessary but NOT sufficient for a leader - compose with
  // canModerateAuthoredContent (contentModeration.ts).
  moderateShowPost: STAFF_OR_LEADER,
  moderateGalleryPhoto: STAFF_OR_LEADER,

  // A leader may add to the schedule; editing their own rehearsal is
  // ownership, resolved at the call site with createdBy.
  createRehearsal: STAFF_OR_LEADER,

  // Adding people is staff-only (addStudentToShow is canManageShow). Leaders
  // held this until October 2026.
  addCastStudent: CAN_MANAGE_SHOW,

  // The People tab - every show member, plain students included. For anyone
  // who is not staff, getCastData sends each person as name, photo, role in
  // the show and leader flag only (memberCastRow in queries.ts), so the
  // safeguarding boundary is that server-side projection, not this row.
  viewCast: ANY_SHOW_MEMBER,

  // Org admins only - getCastData omits the field for everyone else.
  viewCastEmail: new Set<ShowActor>(['orgAdmin', 'superAdmin']),

  // Title, venue, dates, cover, poster, ticket link - staff only (updateShow
  // and the cover/poster callables are canManageShow). Leaders held this until
  // October 2026; the show's Settings are now a staff area.
  editShowDetails: CAN_MANAGE_SHOW,

  // Membership only, by design - students upload gallery photos.
  uploadGalleryPhoto: ANY_SHOW_MEMBER,

  // Membership only - grouping photos travels with adding them
  // (createGalleryFolder in gallery-folders.ts). Renaming, deleting and filing
  // are the creator's or a rank-aware moderator's, resolved at the call site.
  createGalleryFolder: ANY_SHOW_MEMBER,

  // One order for the whole show: staff or a leader of this show, as for the
  // resource library's folders (reorderGalleryFolders in gallery-folders.ts).
  reorderGalleryFolders: STAFF_OR_LEADER,

  // Membership only - anyone in the show may start a group with others in it
  // (createGroupChat in api/groupChats.ts).
  createGroupChat: ANY_SHOW_MEMBER,
};

/** Who holds each global capability, keyed on the raw profile role. */
export const GLOBAL_GRANTS: Readonly<Record<string, ReadonlySet<Role>>> = {
  // show-management.ts - teachers cannot create shows.
  createShow: new Set<Role>(['org_admin', 'super_admin']),
  // deleteOwnAccount.ts - admins are refused so an org is never left unmanaged.
  deleteOwnAccount: new Set<Role>(['student', 'student_leader', 'teacher']),
  // requireBroadcaster (broadcasts/index.ts) and bulkResources/index.ts.
  sendBroadcast: new Set<Role>(['teacher', 'org_admin', 'super_admin']),
};

export const GLOBAL_CAPABILITIES: readonly Capability[] = ['createShow', 'deleteOwnAccount', 'sendBroadcast'];

export function isGlobalCapability(capability: Capability): boolean {
  return GLOBAL_CAPABILITIES.includes(capability);
}

export interface CapabilityContext {
  profile: Pick<Profile, 'role' | 'organizationId' | 'showAssignments'> | null | undefined;
  showId?: string | null;
  showOrganizationId?: string | null;
}

/**
 * Whether the caller holds `capability`, optionally for a show.
 *
 * Fails closed: no profile, no show for a show-scoped capability (admins
 * included - "may manage the show" means nothing with no show), or a missing
 * table row all resolve to false. A false negative hides a control until the
 * profile snapshot lands; a false positive offers a button that errors.
 */
export function resolveCapability(capability: Capability, ctx: CapabilityContext): boolean {
  const { profile, showId, showOrganizationId } = ctx;
  if (!profile) return false;

  if (isGlobalCapability(capability)) {
    return GLOBAL_GRANTS[capability]?.has(profile.role) ?? false;
  }

  if (!showId) return false;
  const actor = actorForShow(profile, showId, showOrganizationId);
  return SHOW_GRANTS[capability]?.has(actor) ?? false;
}
